/* eslint-disable camelcase */
import { Card, CardContent, Grid, Stack } from '@mui/material'
import AgriAction from 'agriAdministration/actions/AgriAction'
import Checkbox from 'components/forms/Checkbox'
import ProgressCard from 'components/card/ProgressCard'
import { StyledFieldSet, StyledLegend } from 'components/StyledElements'
import HomeAction from 'home/actions/HomeAction'
import { isEmpty, orderBy, xor } from 'lodash'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import UsageAction from 'referencial/components/usage/actions/UsageAction'
import DtoUsageAgri from 'referencial/components/usage/dto/DtoUsageAgri'
import i18n from 'simple-react-i18n'
import { getSetting, isTrue } from 'utils/SettingUtils'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import Textarea from 'components/forms/Textarea'
import { CardTitle } from 'components/card/NewCard'
import Select from 'components/forms/Select'

const SurveySettingsApp = ({ params }) => {
    const [dataLoaded, setDataLoaded] = useState(false)
    const [readMode, setReadMode] = useState(true)

    const [step1, setStep1] = useState('1')
    const [step2, setStep2] = useState('1')
    const [step3, setStep3] = useState('1')
    const [consos, setConsos] = useState('1')
    const [usagesReal, setUsagesReal] = useState('1')
    const [usagesPrevi, setUsagesPrevi] = useState('1')
    const [usagesInNotification, setUsagesInNotification] = useState([])
    const [volumesInNotification, setVolumesInNotification] = useState('authorized')
    const [defaultModelDeclaration, setDefaultModelDeclaration] = useState()
    const [defaultModelSynthese, setDefaultModelSynthese] = useState()
    const [defaultModelNotification, setDefaultModelNotification] = useState()
    const [surveyValidationCheckboxLabel, setSurveyValidationCheckboxLabel] = useState(i18n.allSurveyInformationsTrue)

    const {
        survey,
        usages,
        allModelsByType,
    } = useSelector(store => ({
        survey: store.AgriReducer.survey,
        usages: store.UsageReducer.usages,
        allModelsByType: store.AdministrationReducer.allModelsByType,
    }))

    const dispatch = useDispatch()

    useTitle(() => ([{
        title: i18n.planning,
        href: 'planning',
    }, {
        title: i18n.surveys,
        href: 'planning/surveys',
    }, {
        title: `${survey.name || ''} [${survey.year || ''}]`,
        href: `survey/${params.id}`,
    }, {
        title: i18n.settings,
        href: `survey/${params.id}/settings`,
    }]), [])

    const initState = () => {
        const { link_params = [] } = survey
        dispatch(UsageAction.fetchUsages()).then(() => {
            setDataLoaded(true)
            setReadMode(true)
            setStep1(getSetting(link_params, 'step1') || '1')
            setStep2(getSetting(link_params, 'step2') || '1')
            setStep3(getSetting(link_params, 'step3') || '1')
            setConsos(getSetting(link_params, 'consos') || '1')
            setUsagesReal(getSetting(link_params, 'usagesReal') || '1')
            setUsagesPrevi(getSetting(link_params, 'usagesPrevi') || '1')
            setUsagesInNotification((getSetting(link_params, 'usagesInNotification') || '').split(' ').filter((uId) => uId !== ''))
            setVolumesInNotification(getSetting(link_params, 'volumesInNotification') || 'authorized')
            setDefaultModelDeclaration(getSetting(link_params, 'defaultModelDeclaration'))
            setDefaultModelSynthese(getSetting(link_params, 'defaultModelSynthese'))
            setDefaultModelNotification(getSetting(link_params, 'defaultModelNotification'))
            setSurveyValidationCheckboxLabel(getSetting(link_params, 'surveyValidationCheckboxLabel') || i18n.allSurveyInformationsTrue)
        })
    }

    useEffect(() => {
        initState()
    }, [])

    const onSave = () => {
        const settings = [
            { idSurvey: survey.idSurvey, parameter: 'step1', value: step1 },
            { idSurvey: survey.idSurvey, parameter: 'step2', value: step2 },
            { idSurvey: survey.idSurvey, parameter: 'step3', value: step3 },
            { idSurvey: survey.idSurvey, parameter: 'consos', value: consos },
            { idSurvey: survey.idSurvey, parameter: 'usagesReal', value: usagesReal },
            { idSurvey: survey.idSurvey, parameter: 'usagesPrevi', value: usagesPrevi },
            { idSurvey: survey.idSurvey, parameter: 'usagesInNotification', value: usagesInNotification.reduce((acc, i) => `${acc} ${i}`, '') },
            { idSurvey: survey.idSurvey, parameter: 'volumesInNotification', value: volumesInNotification },
            { idSurvey: survey.idSurvey, parameter: 'defaultModelDeclaration', value: defaultModelDeclaration },
            { idSurvey: survey.idSurvey, parameter: 'defaultModelSynthese', value: defaultModelSynthese },
            { idSurvey: survey.idSurvey, parameter: 'defaultModelNotification', value: defaultModelNotification },
            { idSurvey: survey.idSurvey, parameter: 'surveyValidationCheckboxLabel', value: surveyValidationCheckboxLabel },
        ]
        setDataLoaded(false)
        dispatch(AgriAction.updateSurveyParams(survey.idSurvey, settings)).then(() => {
            dispatch(AgriAction.fetchSurvey(survey.idSurvey)).then(() => {
                setDataLoaded(true)
                setReadMode(true)
            })
        })
    }

    useActions(() => {
        if (readMode) return { edit: () => setReadMode(false) }
        return {
            save: onSave,
            cancel: initState,
        }
    }, [dataLoaded, readMode, step1, step2, step3, consos, usagesReal, usagesPrevi, usagesInNotification, volumesInNotification, defaultModelDeclaration,
        defaultModelSynthese, defaultModelNotification, surveyValidationCheckboxLabel])

    const onChangeUsages = (id) => {
        if (!usagesInNotification.includes('all')) {
            if (id === 'all') {
                setUsagesInNotification(['all'])
            } else if (usagesInNotification.includes(id)) {
                setUsagesInNotification(usagesInNotification.filter((uId) => uId !== id))
            } else if (isEmpty(xor(usages.map((u) => `${u.id}`), [...usagesInNotification, id]))) {
                setUsagesInNotification(['all'])
            } else {
                setUsagesInNotification([...usagesInNotification, id])
            }
        } else {
            setUsagesInNotification([...usages.map((u) => `${u.id}`), ...usagesInNotification].filter((uId) => uId !== id).filter((uId) => uId !== 'all'))
        }
    }

    const modelsFormatted = allModelsByType.map((m) => ({
        ...m,
        nameFormatted: m.name.split('.')[0],
    }))

    return (
        <Stack spacing={1} padding={1}>
            {dataLoaded ? (
                <Card>
                    <CardTitle title={i18n.settings} />
                    <CardContent>
                        <StyledFieldSet style={{ margin: 0 }}>
                            <StyledLegend>{i18n.stepSelection}</StyledLegend>
                            <Stack padding={1} spacing={1}>
                                <Checkbox
                                    label={`${i18n.step} 1 : ${i18n.sampler}`}
                                    checked={isTrue(step1)}
                                    onChange={(v) => setStep1(v ? '1' : '0')}
                                    disabled={readMode}
                                />
                                <Checkbox
                                    label={`${i18n.step} 2 : ${i18n.contacts}`}
                                    checked={isTrue(step2)}
                                    onChange={(v) => setStep2(v ? '1' : '0')}
                                    disabled={readMode}
                                />
                                <StyledFieldSet style={{ margin: 0 }}>
                                    <StyledLegend>
                                        <Checkbox
                                            label={`${i18n.step} 3 : ${i18n.catchmentPoints}`}
                                            checked={isTrue(step3)}
                                            onChange={(v) => setStep3(v ? '1' : '0')}
                                            disabled={readMode}
                                        />
                                    </StyledLegend>
                                    <Stack direction={'row'} spacing={2}>
                                        <Checkbox
                                            label={i18n.consos}
                                            checked={isTrue(consos)}
                                            onChange={(v) => setConsos(v ? '1' : '0')}
                                            disabled={readMode}
                                        />
                                        <Checkbox
                                            label={i18n.usagesReal}
                                            checked={isTrue(usagesReal)}
                                            onChange={(v) => setUsagesReal(v ? '1' : '0')}
                                            disabled={readMode}
                                        />
                                        <Checkbox
                                            label={i18n.usagesPrevi}
                                            checked={isTrue(usagesPrevi)}
                                            onChange={(v) => setUsagesPrevi(v ? '1' : '0')}
                                            disabled={readMode}
                                        />
                                    </Stack>
                                </StyledFieldSet>
                            </Stack>
                        </StyledFieldSet>
                        <StyledFieldSet>
                            <StyledLegend>{i18n.usagesShowInNotif}</StyledLegend>
                            <Stack spacing={1} padding={1}>
                                <Checkbox
                                    label={i18n.everybody}
                                    checked={ (usagesInNotification || []).includes('all') }
                                    onChange={() => onChangeUsages('all')}
                                    disabled={readMode}
                                />
                                <Stack direction={'row'} spacing={2}>
                                    {orderBy(usages, 'description').map((u) => (
                                        <Checkbox
                                            label={u.description}
                                            checked={ (usagesInNotification || []).some(r=> [`${u.id}`, 'all'].includes(r)) }
                                            onChange={() => onChangeUsages(`${u.id}`)}
                                            disabled={readMode}
                                            key={u.id}
                                        />
                                    ))}
                                </Stack>
                            </Stack>
                        </StyledFieldSet>
                        <StyledFieldSet>
                            <StyledLegend>{i18n.exportsParameters}</StyledLegend>
                            <Grid container padding={1} spacing={1}>
                                <Grid size={4}>
                                    <Select
                                        label={i18n.defaultModelDeclaration}
                                        options={modelsFormatted.filter((m) => m.stationType === 'agriDeclaration')}
                                        value={defaultModelDeclaration}
                                        onChange={(v) => setDefaultModelDeclaration(v)}
                                        keyName='nameFormatted'
                                        keyValue='name'
                                        disabled={readMode}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Select
                                        label={i18n.defaultModelSynthese}
                                        options={modelsFormatted.filter((m) => m.stationType === 'agriSynthese')}
                                        value={defaultModelSynthese}
                                        onChange={(v) => setDefaultModelSynthese(v)}
                                        keyName='nameFormatted'
                                        keyValue='name'
                                        disabled={readMode}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Select
                                        label={i18n.defaultModelNotification}
                                        options={modelsFormatted.filter((m) => m.stationType === 'agriNotification')}
                                        value={defaultModelNotification}
                                        onChange={(v) => setDefaultModelNotification(v)}
                                        keyName='nameFormatted'
                                        keyValue='name'
                                        disabled={readMode}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Select
                                        label={i18n.volumesShowInNotif}
                                        options={[{
                                            value: 'requested',
                                            label: i18n.requestedVolumes,
                                        }, {
                                            value: 'allocated',
                                            label: i18n.allocatedVolumes,
                                        }, {
                                            value: 'authorized',
                                            label: i18n.authorizedVolumes,
                                        }]}
                                        value={volumesInNotification}
                                        onChange={(v) => setVolumesInNotification(v)}
                                        noNullValue
                                        disabled={readMode}
                                    />
                                </Grid>
                            </Grid>
                        </StyledFieldSet>
                        <StyledFieldSet>
                            <StyledLegend>{i18n.surveyValidationTextSetting}</StyledLegend>
                            <Stack padding={1}>
                                <Textarea
                                    title={i18n.surveyValidationText}
                                    value={surveyValidationCheckboxLabel}
                                    onChange={v => setSurveyValidationCheckboxLabel(v)}
                                    disabled={readMode}
                                />
                            </Stack>
                        </StyledFieldSet>
                    </CardContent>
                </Card>
            ) : <ProgressCard indeterminate round />}
        </Stack>
    )
}

SurveySettingsApp.propTypes = {
    params: PropTypes.shape({
        id: PropTypes.string,
    }),
    usages: PropTypes.arrayOf(PropTypes.instanceOf(DtoUsageAgri)),
}

const mapStateToProps = (store) => {
    return {
        survey: store.AgriReducer.survey,
        usages: store.UsageReducer.usages,
        allModelsByType: store.AdministrationReducer.allModelsByType,
    }
}

const mapDispatchToProps = {
    updateSurveyParams: AgriAction.updateSurveyParams,
    fetchSurvey: AgriAction.fetchSurvey,
    setTitle: HomeAction.setTitle,
    fetchUsages: UsageAction.fetchUsages,
}

export default connect(mapStateToProps, mapDispatchToProps)(SurveySettingsApp)